import React, { useEffect, useState } from 'react';

import { ClockIcon } from 'components/opsVisibility/components/inventoryHistoryV2/filterComponentTailwind/Icons';
import {
    createMonthToDateRange,
    createPastDaysRange,
    createYearToDateRange,
    formatDateForInput,
    formatDateToCustomString,
    formatLocalTimeString,
    isValidDateRange,
} from 'utils/dateUtils/ledgerDateUtils';

import '../filterComponents.css';

function QuickSelectButton({ onClick, timeText, tooltip }) {
    return (
        <button
            onClick={onClick}
            className="relative px-3 py-1 bg-white border border-[var(--shared-black)] text-[var(--shared-black)] focus:outline-none focus:ring-2 focus:ring-[var(--shared-black)] focus:ring-opacity-50 transition-colors dateRangeQuickSelectButton"
            title={tooltip}
        >
            <div className="flex items-center justify-center">
                <ClockIcon />
                <span className="absolute bottom-0 right-0 text-[0.6rem] px-0.5 py-0 bg-white rounded font-bold">
                    {timeText}
                </span>
            </div>
        </button>
    );
}

function DateRangeFilterTW({ currentValue, deletable, filterKey, includeTime = false, label, onFilterChange, type }) {
    const [startDate, setStartDate] = useState(
        currentValue?.data?.startDate ? formatDateForInput(new Date(currentValue.data.startDate), includeTime) : null,
    );
    const [endDate, setEndDate] = useState(
        currentValue?.data?.endDate ? formatDateForInput(new Date(currentValue.data.endDate), includeTime) : null,
    );
    const [error, setError] = useState('');

    // Update local state when currentValue changes
    useEffect(() => {
        if (currentValue?.data) {
            setStartDate(formatDateForInput(new Date(currentValue.data.startDate), includeTime));
            setEndDate(formatDateForInput(new Date(currentValue.data.endDate), includeTime));
        }
    }, [currentValue, includeTime]);

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return includeTime
            ? formatLocalTimeString(date)
            : formatDateToCustomString(date, { includeTime: false, includeTimezone: false });
    };

    const handleStartDateChange = (newStartDate) => {
        setStartDate(newStartDate);

        if (!newStartDate) {
            onFilterChange({
                type,
                data: {
                    startDate: '',
                    endDate,
                },
                filterKey,
                deletable,
            });
            return;
        }

        validateAndSubmitDateRange(newStartDate, endDate);
    };

    const handleEndDateChange = (newEndDate) => {
        setEndDate(newEndDate);

        if (!newEndDate) {
            onFilterChange({
                type,
                data: {
                    startDate,
                    endDate: '',
                },
                filterKey,
                deletable,
            });
            return;
        }

        validateAndSubmitDateRange(startDate, newEndDate);
    };

    const validateAndSubmitDateRange = (start, end) => {
        if (start && end) {
            // Validate dates and set error if needed
            if (!isValidDateRange(new Date(start), new Date(end))) {
                setError('End date cannot be earlier than start date');
                return;
            }

            // Clear any previous error
            setError('');

            // Submit the filter change
            onFilterChange({
                type,
                data: {
                    startDate: start,
                    endDate: end,
                },
                chipLabel: `${label}: ${formatDisplayDate(start)} - ${formatDisplayDate(end)}`,
                filterKey,
                deletable,
            });
        }
    };

    const applyPastDaysRange = (days) => {
        const { endDate: end, startDate: start } = createPastDaysRange(days);

        // Format dates for input fields
        const formattedStart = formatDateForInput(start, includeTime);
        const formattedEnd = formatDateForInput(end, includeTime);

        setStartDate(formattedStart);
        setEndDate(formattedEnd);

        // Clear any errors - these dates are created programmatically so should always be valid
        setError('');

        onFilterChange({
            type,
            data: {
                startDate: formattedStart,
                endDate: formattedEnd,
            },
            chipLabel: `${label}: ${formatDisplayDate(formattedStart)} - ${formatDisplayDate(formattedEnd)}`,
            filterKey,
            deletable,
        });
    };

    // Generic function to handle date range creation
    const applyDateRangeToPreset = (rangeType) => {
        // Create the appropriate date range based on type
        let dateRange;

        if (rangeType === 'month') {
            dateRange = createMonthToDateRange();
        } else if (rangeType === 'year') {
            dateRange = createYearToDateRange();
        } else {
            return; // Invalid range type
        }

        const { endDate: end, startDate: start } = dateRange;

        // Format dates for input fields
        const formattedStart = formatDateForInput(start, includeTime);
        const formattedEnd = formatDateForInput(end, includeTime);

        setStartDate(formattedStart);
        setEndDate(formattedEnd);

        // Clear any errors - these dates are created programmatically so should always be valid
        setError('');

        onFilterChange({
            type,
            data: {
                startDate: formattedStart,
                endDate: formattedEnd,
            },
            chipLabel: `${label}: ${formatDisplayDate(formattedStart)} - ${formatDisplayDate(formattedEnd)}`,
            filterKey,
            deletable,
        });
    };

    return (
        <div className="flex flex-col gap-4 filter-components px-2">
            {/* Quick select buttons */}
            <div className="flex justify-center">
                <div className="inline-flex border border-[var(--shared-black)] rounded overflow-hidden">
                    <QuickSelectButton onClick={() => applyPastDaysRange(1)} timeText="1D" tooltip="Past 1 Day" />
                    <QuickSelectButton onClick={() => applyPastDaysRange(7)} timeText="7D" tooltip="Past 7 Days" />
                    <QuickSelectButton onClick={() => applyPastDaysRange(30)} timeText="30D" tooltip="Past 30 Days" />
                    <QuickSelectButton
                        onClick={() => applyDateRangeToPreset('month')}
                        timeText="MTD"
                        tooltip="Month To Date"
                    />
                    <QuickSelectButton
                        onClick={() => applyDateRangeToPreset('year')}
                        timeText="YTD"
                        tooltip="Year To Date"
                    />
                </div>
            </div>

            {/* Date inputs */}
            <div className={`w-full ${includeTime ? 'flex flex-col' : 'flex flex-col sm:flex-row gap-4'}`}>
                <div className={`${includeTime ? 'w-full mb-4' : 'flex-1'}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                        type={includeTime ? 'datetime-local' : 'date'}
                        value={startDate || ''}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        className={`block w-full border ${
                            error ? 'border-red-500' : 'border-gray-300'
                        } rounded-md standard-shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        max={formatDateForInput(new Date(), includeTime)}
                    />
                    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                </div>
                <div className={`${includeTime ? 'w-full' : 'flex-1'}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                        type={includeTime ? 'datetime-local' : 'date'}
                        value={endDate || ''}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        className={`block w-full border ${
                            error ? 'border-red-500' : 'border-gray-300'
                        } rounded-md standard-shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                </div>
            </div>
        </div>
    );
}

export { DateRangeFilterTW };
